/**
 * Task Store - Zustand store for task state management.
 *
 * Manages tasks, task lists, tags, filtering, sorting, and view state.
 * Wires to TaskService for all CRUD operations (offline-first via IndexedDB).
 * Emits cross-module events via the event bus when tasks complete.
 *
 * @see Requirements 2.1, 2.4, 3.1, 3.3, 3.4, 3.5, 3.6, 10.2
 */

import { create } from 'zustand';
import type { Task, TaskList, Tag, TaskFilters } from '@/types';
import type { TaskSort } from '@/services/taskService';
import {
  createTask as serviceCreateTask,
  updateTask as serviceUpdateTask,
  deleteTask as serviceDeleteTask,
  listTasks as serviceListTasks,
  searchTasks as serviceSearchTasks,
  createTaskList as serviceCreateTaskList,
  getTaskLists as serviceGetTaskLists,
  updateTaskList as serviceUpdateTaskList,
  deleteTaskList as serviceDeleteTaskList,
  createTag as serviceCreateTag,
  getTags as serviceGetTags,
  deleteTag as serviceDeleteTag,
} from '@/services/taskService';
import { eventBus } from '@/services/eventBus';

// ─── Types ───────────────────────────────────────────────────────────────────

/** Available views in the Tasks module. */
export type TaskView = 'inbox' | 'today' | 'upcoming' | 'list' | 'eisenhower';

export interface TaskState {
  /** All loaded tasks (non-deleted). */
  tasks: Task[];
  /** User's task lists. */
  taskLists: TaskList[];
  /** User's tags. */
  tags: Tag[];
  /** Current active filters. */
  filters: TaskFilters;
  /** Current sort configuration. */
  sort: TaskSort;
  /** Active view in the Tasks module. */
  activeView: TaskView;
  /** Currently selected task ID for detail panel. */
  selectedTaskId: string | null;
  /** Whether a task operation is in progress. */
  isLoading: boolean;
  /** Last error message from a task operation. */
  error: string | null;
}

export interface TaskActions {
  /** Load tasks from the local store with current filters and sort. */
  loadTasks: () => Promise<void>;
  /** Create a new task. */
  createTask: (
    task: Omit<Task, 'taskId' | 'createdAt' | 'updatedAt' | 'syncStatus'>
  ) => Promise<Task>;
  /** Update an existing task by ID. */
  updateTask: (taskId: string, updates: Partial<Task>) => Promise<void>;
  /** Soft-delete a task. */
  deleteTask: (taskId: string) => Promise<void>;
  /** Mark a task as complete (cascades to subtasks, emits event). */
  completeTask: (taskId: string) => Promise<void>;

  /** Load all task lists. */
  loadTaskLists: () => Promise<void>;
  /** Create a new task list. */
  createTaskList: (
    list: Omit<TaskList, 'listId' | 'createdAt' | 'updatedAt'>
  ) => Promise<TaskList>;
  /** Update an existing task list. */
  updateTaskList: (listId: string, updates: Partial<TaskList>) => Promise<void>;
  /** Delete a task list. */
  deleteTaskList: (listId: string) => Promise<void>;

  /** Load all tags. */
  loadTags: () => Promise<void>;
  /** Create a new tag. */
  createTag: (tag: Omit<Tag, 'tagId'>) => Promise<Tag>;
  /** Delete a tag. */
  deleteTag: (tagId: string) => Promise<void>;

  /** Set filter criteria and reload tasks. */
  setFilters: (filters: TaskFilters) => void;
  /** Set sort configuration and reload tasks. */
  setSort: (sort: TaskSort) => void;
  /** Set the active view. */
  setActiveView: (view: TaskView) => void;
  /** Select a task (for detail panel). */
  selectTask: (taskId: string | null) => void;
  /** Search tasks by text query. */
  searchTasks: (query: string) => Promise<void>;
}

export type TaskStore = TaskState & TaskActions;

// ─── Store ───────────────────────────────────────────────────────────────────

export const useTaskStore = create<TaskStore>((set, get) => ({
  // ── State ──────────────────────────────────────────────────────────────────
  tasks: [],
  taskLists: [],
  tags: [],
  filters: {},
  sort: { field: 'createdAt', direction: 'desc' },
  activeView: 'inbox',
  selectedTaskId: null,
  isLoading: false,
  error: null,

  // ── Task Actions ───────────────────────────────────────────────────────────

  loadTasks: async () => {
    set({ isLoading: true, error: null });
    try {
      const { filters, sort } = get();
      const tasks = await serviceListTasks(filters, sort);
      set({ tasks, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load tasks';
      set({ isLoading: false, error: message });
    }
  },

  createTask: async (task) => {
    set({ error: null });
    try {
      const newTask = await serviceCreateTask(task);
      set({ tasks: [...get().tasks, newTask] });
      return newTask;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create task';
      set({ error: message });
      throw error;
    }
  },

  updateTask: async (taskId, updates) => {
    set({ error: null });
    try {
      const updatedTask = await serviceUpdateTask(taskId, updates);
      set({
        tasks: get().tasks.map((t) => (t.taskId === taskId ? updatedTask : t)),
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update task';
      set({ error: message });
      throw error;
    }
  },

  deleteTask: async (taskId) => {
    set({ error: null });
    try {
      await serviceDeleteTask(taskId);
      set({
        tasks: get().tasks.filter((t) => t.taskId !== taskId),
        selectedTaskId: get().selectedTaskId === taskId ? null : get().selectedTaskId,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete task';
      set({ error: message });
      throw error;
    }
  },

  completeTask: async (taskId) => {
    set({ error: null });
    try {
      const task = get().tasks.find((t) => t.taskId === taskId);
      if (!task) {
        throw new Error(`Task not found: ${taskId}`);
      }

      const now = new Date().toISOString();

      // Mark all incomplete subtasks as complete (cascade)
      const completedSubtasks = task.subtasks.map((st) =>
        st.isCompleted ? st : { ...st, isCompleted: true, completedAt: now }
      );

      const updates: Partial<Task> = {
        isCompleted: true,
        completedAt: now,
        subtasks: completedSubtasks,
      };

      const updatedTask = await serviceUpdateTask(taskId, updates);
      set({
        tasks: get().tasks.map((t) => (t.taskId === taskId ? updatedTask : t)),
      });

      // Emit 'task:completed' event via the event bus for cross-module integration
      eventBus.emit('task:completed', {
        taskId: updatedTask.taskId,
        taskTitle: updatedTask.title,
        completedAt: now,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to complete task';
      set({ error: message });
      throw error;
    }
  },

  // ── Task List Actions ──────────────────────────────────────────────────────

  loadTaskLists: async () => {
    set({ error: null });
    try {
      const taskLists = await serviceGetTaskLists();
      set({ taskLists });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load task lists';
      set({ error: message });
    }
  },

  createTaskList: async (list) => {
    set({ error: null });
    try {
      const newList = await serviceCreateTaskList(list);
      set({ taskLists: [...get().taskLists, newList] });
      return newList;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create task list';
      set({ error: message });
      throw error;
    }
  },

  updateTaskList: async (listId, updates) => {
    set({ error: null });
    try {
      const updatedList = await serviceUpdateTaskList(listId, updates);
      set({
        taskLists: get().taskLists.map((l) => (l.listId === listId ? updatedList : l)),
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update task list';
      set({ error: message });
      throw error;
    }
  },

  deleteTaskList: async (listId) => {
    set({ error: null });
    try {
      await serviceDeleteTaskList(listId);
      set({
        taskLists: get().taskLists.filter((l) => l.listId !== listId),
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete task list';
      set({ error: message });
      throw error;
    }
  },

  // ── Tag Actions ────────────────────────────────────────────────────────────

  loadTags: async () => {
    set({ error: null });
    try {
      const tags = await serviceGetTags();
      set({ tags });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load tags';
      set({ error: message });
    }
  },

  createTag: async (tag) => {
    set({ error: null });
    try {
      const newTag = await serviceCreateTag(tag);
      set({ tags: [...get().tags, newTag] });
      return newTag;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create tag';
      set({ error: message });
      throw error;
    }
  },

  deleteTag: async (tagId) => {
    set({ error: null });
    try {
      await serviceDeleteTag(tagId);
      set({
        tags: get().tags.filter((t) => t.tagId !== tagId),
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete tag';
      set({ error: message });
      throw error;
    }
  },

  // ── Filter / Sort / View Actions ──────────────────────────────────────────

  setFilters: (filters) => {
    set({ filters });
    // Reload tasks with new filters
    get().loadTasks();
  },

  setSort: (sort) => {
    set({ sort });
    // Reload tasks with new sort
    get().loadTasks();
  },

  setActiveView: (view) => {
    set({ activeView: view });
  },

  selectTask: (taskId) => {
    set({ selectedTaskId: taskId });
  },

  searchTasks: async (query) => {
    set({ isLoading: true, error: null });
    try {
      const tasks = await serviceSearchTasks(query);
      set({ tasks, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to search tasks';
      set({ isLoading: false, error: message });
    }
  },
}));
